import { Check, Copy, RotateCw, SquarePen, type LucideIcon } from 'lucide-react';
import { useEffect, useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/Domain/components/Shared/Tooltip/Tooltip';
import { useCopiedFeedback } from '@/Domain/hooks/App/useCopiedFeedback.hook';
import { cn } from '@/Domain/utils/cn';
import { copyToClipboard } from '@/Domain/utils/copyToClipboard';

type SafariBrowserFrameProps = {
  url?: string;
  children: ReactNode;
  /** Optional element(s) rendered on the right side of the browser chrome (mirrors
   *  the position of the traffic-light dots on the left). */
  headerAction?: ReactNode;
  /** Hide the macOS-style traffic-light window controls. Useful in mobile preview
   *  where a phone-shaped chrome shouldn't show desktop window dots. */
  hideWindowControls?: boolean;
  /** Makes the address bar editable; commits the new URL on Enter/blur. */
  onUrlChange?: (url: string) => void;
  /** Makes the address a click-to-copy button for this value. */
  copyValue?: string;
  /** When set, a browser-style refresh button appears left of the address bar. */
  onRefresh?: () => void;
  isRefreshing?: boolean;
  /** Fixed content aspect ratio (e.g. aspect-[16/9]); unset fills parent height. */
  contentAspect?: string;
  /** Mobile-browser look: address bar at the bottom, stripped of desktop chrome. */
  isMobile?: boolean;
};

// Mobile address-bar height (px). Shared so the widget viewport can shrink by it and anchor the launcher above the bar.
export const MOBILE_ADDRESS_BAR_HEIGHT = 52;

const ADDRESS_CONTAINER =
  'flex items-center justify-center bg-black/[0.02] dark:bg-white/[0.04] border border-border rounded-md px-3 py-1 w-full min-w-0';
// On the frosted mobile bar the default faint fill/border muddies against the blur; use an inset
// pill with a slightly stronger border (darker in light, lighter in dark) so it reads cleanly over the blur.
const GLASS_ADDRESS_CONTAINER =
  'bg-background/70 dark:bg-white/10 border-black/10 dark:border-white/20';

const BrowserAddress = ({
  url,
  onUrlChange,
  copyValue,
  disabled,
  onGlass = false,
}: {
  url?: string;
  onUrlChange?: (url: string) => void;
  copyValue?: string;
  disabled?: boolean;
  onGlass?: boolean;
}) => {
  const { t } = useTranslation('website');
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(url ?? '');
  const { copied, markCopied } = useCopiedFeedback();
  useEffect(() => {
    if (!editing) setDraft(url ?? '');
  }, [url, editing]);

  // Higher-contrast idle text on glass so the URL stays legible over the blur.
  const containerClass = cn(ADDRESS_CONTAINER, onGlass && GLASS_ADDRESS_CONTAINER);
  const idleTextClass = onGlass ? 'text-muted-foreground' : 'text-foreground-subtle';

  if (copyValue) {
    const handleCopy = () => {
      if (copied) return;
      copyToClipboard(copyValue, markCopied, () =>
        console.error('Could not copy link to clipboard'),
      );
    };
    return (
      <button
        type="button"
        onClick={handleCopy}
        aria-label={t('preview_controls.click_to_copy')}
        data-testid="preview-address-copy"
        className={cn(
          containerClass,
          'group/addr cursor-pointer transition-colors hover:bg-black/[0.08] dark:hover:bg-white/10',
        )}
      >
        {copied ? (
          <span className="inline-flex items-center gap-1.5 text-xs text-foreground">
            <Check className="size-3" />
            {t('common:copied')}
          </span>
        ) : (
          <>
            <span className={cn('min-w-0 truncate text-xs group-hover/addr:hidden', idleTextClass)}>
              {url}
            </span>
            <span className="hidden items-center gap-1.5 text-xs text-foreground group-hover/addr:inline-flex">
              <Copy className="size-3" />
              {t('preview_controls.click_to_copy')}
            </span>
          </>
        )}
      </button>
    );
  }

  // Read-only while capturing: editing would kick off a second, contradicting capture.
  if (!onUrlChange || disabled) {
    return (
      <div className={containerClass}>
        <span className={cn('text-xs truncate min-w-0', idleTextClass)}>{url}</span>
      </div>
    );
  }

  const commit = () => {
    setEditing(false);
    const next = draft.trim();
    if (next !== (url ?? '')) onUrlChange(next);
  };

  if (editing) {
    return (
      <div className={containerClass}>
        <input
          autoFocus
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onBlur={commit}
          onKeyDown={(e) => {
            if (e.key === 'Enter') commit();
            else if (e.key === 'Escape') {
              setDraft(url ?? '');
              setEditing(false);
            }
          }}
          placeholder="https://yourwebsite.com"
          aria-label={t('preview_controls.address')}
          data-testid="preview-address-input"
          className="w-full min-w-0 bg-transparent text-center text-xs text-foreground outline-none"
        />
      </div>
    );
  }

  return (
    <button
      type="button"
      onClick={() => setEditing(true)}
      aria-label={t('preview_controls.edit_address')}
      data-testid="preview-address-edit"
      className={cn(
        containerClass,
        'group/addr cursor-text transition-colors hover:bg-black/[0.08] dark:hover:bg-white/10',
      )}
    >
      <span className={cn('min-w-0 truncate text-xs group-hover/addr:hidden', idleTextClass)}>
        {url || 'https://yourwebsite.com'}
      </span>
      <span className="hidden items-center gap-1.5 text-xs text-foreground group-hover/addr:inline-flex">
        <SquarePen className="size-3" />
        {t('preview_controls.click_to_edit')}
      </span>
    </button>
  );
};

/** Icon-only browser-chrome button in a tooltip; the tooltip prop overrides the aria label. */
export const BrowserIconButton = ({
  icon: Icon,
  label,
  onClick,
  tooltip,
  spinning,
  disabled,
  ...props
}: {
  icon: LucideIcon;
  label: string;
  onClick: () => void;
  tooltip?: ReactNode;
  spinning?: boolean;
  disabled?: boolean;
  'data-testid'?: string;
}) => (
  <TooltipProvider delayDuration={300}>
    <Tooltip>
      <TooltipTrigger asChild>
        <button
          type="button"
          onClick={onClick}
          disabled={disabled}
          aria-label={label}
          className={cn(
            'shrink-0 rounded-md p-1 text-muted-foreground transition-colors',
            disabled
              ? 'cursor-not-allowed opacity-50'
              : 'cursor-pointer hover:bg-accent hover:!text-foreground',
          )}
          {...props}
        >
          <Icon className={cn('size-3.5', spinning && 'animate-spin')} />
        </button>
      </TooltipTrigger>
      <TooltipContent>{tooltip ?? label}</TooltipContent>
    </Tooltip>
  </TooltipProvider>
);

const BrowserRefreshButton = ({
  onRefresh,
  spinning,
  disabled,
}: {
  onRefresh: () => void;
  spinning?: boolean;
  disabled?: boolean;
}) => {
  const { t } = useTranslation('website');
  return (
    <BrowserIconButton
      icon={RotateCw}
      label={t('preview_controls.refresh_website')}
      onClick={onRefresh}
      spinning={spinning}
      disabled={disabled}
      data-testid="preview-refresh"
    />
  );
};

export const SafariBrowserFrame = ({
  url,
  children,
  headerAction,
  hideWindowControls = false,
  onUrlChange,
  copyValue,
  onRefresh,
  isRefreshing,
  contentAspect,
  isMobile = false,
}: SafariBrowserFrameProps) => {
  // Mobile: the address bar is overlaid on the screenshot's bottom (frosted, like Safari) so the phone frame keeps the screenshot's aspect ratio instead of growing ~55px taller.
  const mobileAddressBar = (
    <div
      className="absolute inset-x-0 bottom-0 flex items-center gap-2 border-t border-border/60 bg-background/75 px-4 backdrop-blur-xl backdrop-saturate-150 dark:bg-background/60"
      style={{ height: MOBILE_ADDRESS_BAR_HEIGHT }}
    >
      <div className="flex-1 min-w-0">
        <BrowserAddress
          url={url}
          onUrlChange={onUrlChange}
          copyValue={copyValue}
          disabled={isRefreshing}
          onGlass
        />
      </div>
      {onRefresh && (
        <BrowserRefreshButton
          onRefresh={onRefresh}
          spinning={isRefreshing}
          disabled={isRefreshing}
        />
      )}
      {headerAction}
    </div>
  );

  const content = (
    <div
      className={cn(
        'overflow-hidden',
        // aspect-ratio boxes give no definite height to h-full children; fill via absolute positioning.
        contentAspect ? cn('relative w-full', contentAspect) : 'flex-1',
      )}
    >
      {contentAspect ? <div className="absolute inset-0">{children}</div> : children}
      {isMobile && mobileAddressBar}
    </div>
  );

  const chrome = (
    <div className="flex items-center gap-2 px-4 py-3 bg-muted border-b border-border">
      {!hideWindowControls && (
        <div className="flex items-center gap-1.5 mr-2">
          <div className="size-3 rounded-full bg-[#FF5F57]" />
          <div className="size-3 rounded-full bg-[#FEBC2E]" />
          <div className="size-3 rounded-full bg-[#28C840]" />
        </div>
      )}

      <div className="flex-1 flex items-center justify-center gap-2 min-w-0">
        <BrowserAddress
          url={url}
          onUrlChange={onUrlChange}
          copyValue={copyValue}
          disabled={isRefreshing}
        />
        {onRefresh && (
          <BrowserRefreshButton
            onRefresh={onRefresh}
            spinning={isRefreshing}
            disabled={isRefreshing}
          />
        )}
      </div>

      <div className="flex items-center justify-end gap-1 shrink-0">{headerAction}</div>
    </div>
  );

  // @container so chrome buttons' container-query truncation uses the frame width.
  return (
    <div
      className={cn(
        '@container flex flex-col w-full overflow-hidden border border-border bg-card shadow-sm',
        isMobile ? 'rounded-[1.75rem]' : 'rounded-xl',
        contentAspect ? undefined : 'h-full',
      )}
    >
      {isMobile ? (
        content
      ) : (
        <>
          {chrome}
          {content}
        </>
      )}
    </div>
  );
};
